import { Component, DestroyRef, HostListener, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs';
import { LucideAngularModule } from 'lucide-angular';
import {
  AuthService,
  BillingGateService,
  CompanyContextService,
  I18nService,
  TenantService,
} from '../../../../core';
import { TPipe } from '../../../../shared/pipes';
import { resolveApiAssetUrl } from '../../../../shared/utils/resolve-api-asset-url.util';
import { CompanyStore } from '../../store/company.store';

@Component({
  selector: 'app-company-switcher',
  standalone: true,
  imports: [LucideAngularModule, TPipe, RouterLink, RouterLinkActive],
  template: `
    <div class="company-switcher-wrapper">
      @if (showInteractiveWorkspace()) {
        <button
          type="button"
          class="company-switcher"
          [class.is-open]="isOpen()"
          [attr.aria-label]="ariaLabel()"
          [attr.aria-expanded]="isOpen()"
          [attr.aria-haspopup]="true"
          (click)="toggle($event)"
        >
          <span class="company-avatar" aria-hidden="true">
            @if (activeCompanyLogoSrc()) {
              <img
                [src]="activeCompanyLogoSrc()"
                [alt]="activeCompany()?.companyName ?? ''"
                class="company-logo"
              />
            } @else {
              {{ initials() }}
            }
          </span>

          <span class="company-text">
            <span class="company-label">{{ 'domain.company' | t }}</span>
            <span class="company-name">{{ activeCompanyName() }}</span>
          </span>

          <span class="company-chevron" aria-hidden="true">
            <lucide-icon name="chevrons-up-down" [size]="14"></lucide-icon>
          </span>
        </button>

        @if (isOpen()) {
          <div class="company-workspace-menu" role="menu">
            @if (showCompanyWorkspaceLinks()) {
              @if (routeCompanyKey(); as ck) {
                <a
                  [routerLink]="['/c', ck, 'empresa', 'dados']"
                  routerLinkActive="is-active"
                  [routerLinkActiveOptions]="{ exact: true }"
                  class="company-menu-item"
                  role="menuitem"
                  (click)="close()"
                >
                  <lucide-icon name="building-2" class="h-4 w-4"></lucide-icon>
                  <span>{{ 'nav.dadosdaempresa' | t }}</span>
                </a>
                <a
                  [routerLink]="['/c', ck, 'company-hub']"
                  routerLinkActive="is-active"
                  [routerLinkActiveOptions]="{ exact: false }"
                  class="company-menu-item company-menu-item--secondary"
                  role="menuitem"
                  (click)="close()"
                >
                  <lucide-icon name="settings-2" class="h-4 w-4"></lucide-icon>
                  <span>{{ 'nav.preferencias' | t }}</span>
                </a>
              }
            }

            @if (canManageCompanyWorkspace() && canSwitchCompanies()) {
              <div class="company-menu-divider company-menu-divider-tight"></div>
            }

            @if (isBillingBlocked() && memberships().length > 1) {
              <div class="company-menu-divider company-menu-divider-tight"></div>
              <div class="company-state">{{ 'billing.gate.switcherDisabled' | t }}</div>
            }

            @if (canSwitchCompanies()) {
              @if (loading()) {
                <div class="company-state">{{ 'common.saving' | t }}</div>
              } @else if (error()) {
                <div class="company-state">{{ error() }}</div>
              } @else if (!memberships().length) {
                <div class="company-state">Nenhuma empresa disponivel.</div>
              } @else {
                <div class="company-list">
                  @for (membership of memberships(); track membership.companyId) {
                    <button
                      type="button"
                      class="company-item"
                      [class.is-active]="activeCompanyId() === membership.companyId"
                      (click)="onSelectCompany(membership.companyId)"
                      role="menuitem"
                    >
                      <span class="company-item-avatar" aria-hidden="true">
                        @if (membershipLogoSrc(membership.companyLogo)) {
                          <img
                            [src]="membershipLogoSrc(membership.companyLogo)"
                            [alt]="membership.companyName"
                            class="company-logo"
                          />
                        } @else {
                          {{ getInitials(membership.companyName) }}
                        }
                      </span>
                      <span class="company-item-text">
                        <span class="company-item-name">{{ membership.companyName }}</span>
                        <span class="company-item-meta">{{ membershipRoleLabel(membership.role) }}</span>
                      </span>
                      @if (activeCompanyId() === membership.companyId) {
                        <lucide-icon name="check" class="h-4 w-4 company-item-check"></lucide-icon>
                      }
                    </button>
                  }
                </div>
              }
            }
          </div>
        }
      } @else {
        <div class="company-switcher company-switcher--static" role="group" [attr.aria-label]="ariaLabel()">
          <span class="company-avatar" aria-hidden="true">
            @if (activeCompanyLogoSrc()) {
              <img
                [src]="activeCompanyLogoSrc()"
                [alt]="activeCompany()?.companyName ?? ''"
                class="company-logo"
              />
            } @else {
              {{ initials() }}
            }
          </span>

          <span class="company-text">
            <span class="company-label">{{ 'domain.company' | t }}</span>
            <span class="company-name">{{ activeCompanyName() }}</span>
          </span>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .company-switcher-wrapper {
        position: relative;
        width: 100%;
      }

      .company-switcher {
        width: 100%;
        min-height: 52px;
        border: 1px solid var(--dh-line);
        background: var(--dh-bg-elev);
        border-radius: 10px;
        padding: 10px 12px;
        display: grid;
        grid-template-columns: auto 1fr auto;
        align-items: center;
        gap: 12px;
        text-align: left;
        cursor: pointer;
        box-shadow: none;
        transition:
          background-color 150ms ease,
          border-color 150ms ease,
          box-shadow 150ms ease;
      }

      .company-switcher:hover {
        background: var(--dh-bg-sunken);
        border-color: var(--dh-line-strong);
      }

      .company-switcher.is-open {
        background: var(--dh-bg-sunken);
        border-color: var(--dh-line-strong);
      }

      .company-switcher:focus {
        outline: none;
      }

      .company-switcher:focus-visible {
        box-shadow: 0 0 0 2px var(--focus), var(--shadow-sm);
      }

      .company-avatar {
        width: 32px;
        height: 32px;
        border-radius: 8px;
        border: 0;
        background: var(--dh-ok-soft);
        color: var(--dh-ok);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-family: 'Inter', sans-serif;
        font-size: 11px;
        font-weight: 700;
        overflow: hidden;
        flex-shrink: 0;
      }

      .company-avatar lucide-icon {
        width: 16px;
        height: 16px;
        line-height: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .company-item-avatar {
        width: 32px;
        height: 32px;
        border-radius: 8px;
        border: 0;
        background: var(--dh-bg-sunken);
        color: var(--dh-ink-2);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-family: 'Inter', sans-serif;
        font-size: 11px;
        font-weight: 700;
        overflow: hidden;
        flex-shrink: 0;
      }

      .company-logo {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .company-text {
        min-width: 0;
        display: grid;
        gap: 3px;
      }

      .company-label {
        font-family: 'Inter', sans-serif;
        font-size: 9.5px;
        font-weight: 500;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--dh-ink-3);
        line-height: 1;
      }

      .company-name {
        font-family: 'Inter', sans-serif;
        font-size: 13px;
        color: var(--dh-ink);
        font-weight: 550;
        letter-spacing: 0;
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .company-chevron {
        color: var(--dh-ink-4);
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .company-switcher--static {
        cursor: default;
        pointer-events: none;
      }

      .company-switcher--static:hover {
        border-color: var(--border-subtle);
        box-shadow: var(--shadow-sm);
      }

      .company-workspace-menu {
        position: absolute;
        left: 0;
        right: 0;
        top: calc(100% + 8px);
        min-width: 100%;
        border-radius: var(--r-card);
        border: 1px solid var(--border-subtle);
        background: var(--bg-surface);
        box-shadow: var(--shadow-elevated);
        padding: 8px 10px 10px;
        z-index: 50;
      }

      .company-menu-divider {
        height: 1px;
        background: var(--border-subtle);
        margin: 0 2px 8px;
      }

      .company-menu-divider-tight {
        margin-top: 4px;
        margin-bottom: 8px;
      }

      .company-menu-item {
        width: 100%;
        min-height: 40px;
        border: 0;
        background: transparent;
        border-radius: var(--r-control);
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: flex-start;
        gap: 10px;
        padding: 0 12px;
        color: var(--text);
        font-size: 14px;
        font-weight: 500;
        text-decoration: none;
        box-sizing: border-box;
        transition: background-color var(--transition-fast);
      }

      .company-menu-item:hover {
        background: var(--hover);
      }

      .company-menu-item.is-active {
        background: color-mix(in srgb, var(--accent-soft) 70%, var(--bg-surface));
        color: var(--text);
        box-shadow: inset 3px 0 0 var(--active-text);
      }

      .company-menu-item.is-active lucide-icon {
        color: var(--active-text);
      }

      .company-menu-item lucide-icon {
        color: var(--muted);
        flex-shrink: 0;
      }

      .company-menu-item--secondary {
        margin-top: 2px;
        font-size: 13px;
      }

      .company-state {
        padding: 8px 10px;
        font-size: 12px;
        color: var(--muted);
      }

      .company-list {
        display: grid;
        gap: 2px;
      }

      .company-item {
        width: 100%;
        border: 0;
        background: transparent;
        padding: 6px 8px;
        display: grid;
        grid-template-columns: auto 1fr auto;
        align-items: center;
        gap: 7px;
        text-align: left;
        cursor: pointer;
        border-radius: 9px;
      }

      .company-item:hover {
        background: var(--hover);
      }

      .company-item.is-active {
        background: color-mix(in srgb, var(--accent-soft) 55%, transparent);
        box-shadow: inset 3px 0 0 var(--active-text);
      }

      .company-item-text {
        min-width: 0;
        display: grid;
      }

      .company-item-name {
        font-size: 12.5px;
        color: var(--text);
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .company-item-meta {
        font-size: 10.5px;
        color: var(--muted);
      }

      .company-item-check {
        color: var(--active-text);
      }

      @media (max-width: 768px) {
        .company-switcher-wrapper {
          max-width: none;
        }
      }
    `,
  ],
})
export class CompanySwitcherComponent {
  private readonly i18n = inject(I18nService);
  private readonly auth = inject(AuthService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly companyStore = inject(CompanyStore);
  private readonly tenant = inject(TenantService);
  private readonly router = inject(Router);
  private readonly billingGate = inject(BillingGateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentPath = signal(this.router.url);

  readonly isOpen = signal(false);
  readonly memberships = this.companyStore.memberships;
  readonly activeCompanyId = this.companyStore.activeCompanyId;
  readonly activeCompanyKey = this.companyStore.activeCompanyKey;
  readonly activeCompany = this.companyStore.activeCompany;
  readonly loading = this.companyStore.loading;
  readonly error = this.companyStore.error;
  readonly t = this.i18n.t.bind(this.i18n);
  // Enquanto o billing gate estiver ativo, a troca de empresa fica desabilitada
  // para evitar escapar do fluxo de pagamento via seletor.
  readonly isBillingBlocked = this.billingGate.blocked;
  readonly canSwitchCompanies = computed(
    () => this.memberships().length > 1 && !this.isBillingBlocked()
  );

  /** Dados da empresa + preferências: só **Admin** (`canAccessCompanyWorkspaceMenu`). */
  readonly canManageCompanyWorkspace = computed(() => this.companyContext.canAccessCompanyWorkspaceMenu());

  /** Links «dados da empresa» / «preferências»; precisa de chave de rota. */
  readonly showCompanyWorkspaceLinks = computed(
    () => this.canManageCompanyWorkspace() && !!this.routeCompanyKey()
  );

  /** Chevron: **Admin** na sessão/JWT/membership ou **várias empresas**. */
  readonly showInteractiveWorkspace = computed(
    () => this.canManageCompanyWorkspace() || this.canSwitchCompanies()
  );

  /**
   * Chave `companyKey` para rotas `/c/:key/...` quando o store ainda não expõe `activeCompanyKey`.
   */
  readonly routeCompanyKey = computed(() => {
    void this.currentPath();
    const fromStore = this.activeCompanyKey();
    if (fromStore?.trim()) return fromStore.trim();
    const fromTenant = this.tenant.companyKey();
    if (fromTenant?.trim()) return fromTenant.trim();
    const ac = this.tenant.activeCompany();
    const fromName = ac?.name?.trim() ? this.slugifyRouteKey(ac.name) : '';
    if (fromName) return fromName;
    const m = this.router.url.match(/\/c\/([^/?#]+)/);
    const fromUrl = m?.[1] ? decodeURIComponent(m[1]).trim() : '';
    return fromUrl || null;
  });

  readonly activeCompanyLogoSrc = computed(() =>
    resolveApiAssetUrl(this.activeCompany()?.companyLogo ?? '')
  );

  readonly activeCompanyName = computed(() => {
    const fallbackLabel = this.t('domain.company');
    const storeName = this.activeCompany()?.companyName?.trim() ?? '';
    const contextName = this.companyContext.activeCompany()?.name?.trim() ?? '';
    const sessionName = this.auth.currentUser()?.companyName?.trim() ?? '';

    const fromStore = this.normalizeCompanyName(storeName, fallbackLabel);
    if (fromStore) return fromStore;

    const fromContext = this.normalizeCompanyName(contextName, fallbackLabel);
    if (fromContext) return fromContext;

    const fromSession = this.normalizeCompanyName(sessionName, fallbackLabel);
    if (fromSession) return fromSession;

    return fallbackLabel;
  });

  readonly initials = computed(() => this.getInitials(this.activeCompanyName()));

  readonly ariaLabel = computed(() =>
    this.t('layout.sidebar.workspaceAria', { name: this.activeCompanyName() })
  );

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.currentPath.set(this.router.url));
  }

  toggle(event: MouseEvent): void {
    event.stopPropagation();
    this.isOpen.update((value) => !value);
  }

  close(): void {
    this.isOpen.set(false);
  }

  membershipRoleLabel(role: string): string {
    const r = role.trim().toLowerCase();
    if (r === 'owner') return this.t('settings.members.roles.owner');
    if (r === 'admin') return this.t('settings.members.roles.admin');
    return this.t('settings.members.roles.member');
  }

  async onSelectCompany(companyId: string): Promise<void> {
    this.isOpen.set(false);
    if (companyId === this.activeCompanyId()) return;
    // Defesa em profundidade: o botão só renderiza quando `canSwitchCompanies` é true,
    // mas garante que nenhuma chamada vaza enquanto o gate estiver ativo.
    if (this.isBillingBlocked()) return;
    await this.companyContext.switchCompany(companyId);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen()) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('app-company-switcher')) return;
    this.isOpen.set(false);
  }

  membershipLogoSrc(companyLogo: string | null | undefined): string {
    return resolveApiAssetUrl(companyLogo ?? '');
  }

  getInitials(name: string): string {
    const parts = name
      .split(/\s+/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    return (parts[0]?.slice(0, 2) ?? 'CO').toUpperCase();
  }

  private normalizeCompanyName(value: string, fallbackLabel: string): string {
    if (!value) return '';
    const normalized = value.trim().toLowerCase();
    const fallbackNormalized = fallbackLabel.trim().toLowerCase();
    if (!normalized || normalized === fallbackNormalized) return '';
    if (normalized === 'empresa' || normalized === 'company') return '';
    // Defesa contra CNPJ/CPF caindo no campo nome (já vimos um caso onde a sessão
    // vem com taxId no companyName). Se for só dígitos com tamanho de CPF (11) ou
    // CNPJ (14, com ou sem máscara), pula essa fonte e tenta a próxima.
    const onlyDigits = normalized.replace(/[^\d]/g, '');
    const isPureNumeric = onlyDigits === normalized.replace(/\D/g, '') && /^\d+$/.test(normalized.replace(/\D/g, ''));
    if (isPureNumeric && (onlyDigits.length === 11 || onlyDigits.length === 14)) return '';
    return value.trim();
  }

  private slugifyRouteKey(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }
}
